import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { TextTooltip } from '@mdvip/shared/components/blocks/TextTooltip';
import { Alert, AlertDescription, AlertTitle } from '@mdvip/shared/components/ui/alert';
import { Button } from '@mdvip/shared/components/ui/button';
import { Skeleton } from '@mdvip/shared/components/ui/skeleton';
import { useCurrency } from '@mdvip/shared/hooks/useCurrency';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { MDVIP_COMPANY_INFO } from '@mdvip/shared/lib/mdvip-company-info';
import { useTranslation } from 'react-i18next';
import { PaymentPlanError } from '@/hooks/payments/paymentPlan/paymentPlanApi';
import { usePaymentPlan } from '@/hooks/payments/paymentPlan/usePaymentPlan';
import { Metric } from './Metric';

interface PaymentPlanMetricProps {
  /** Which of PaymentSummaryCard's two responsive layouts this instance renders. Both mount
   *  simultaneously (CSS visibility, not conditional) — this only changes the markup shape. */
  layout: 'narrow' | 'wide';
  /** Positional padding for the wide 3-column grid, or the narrow full-width strip below the row
   *  — owned by the parent grid, not this metric. */
  className?: string;
}

/**
 * Payment Plan metric for the "Payment summary" card — the member's billing cadence and annual
 * membership fee. Owns its own loading/error state, independent of the other two metrics.
 */
export function PaymentPlanMetric({ layout, className }: PaymentPlanMetricProps) {
  const { t } = useTranslation('members');
  const tError = useErrorTranslation();
  const { data, isLoading, isError, error, refetch } = usePaymentPlan();
  const { formatCurrency } = useCurrency();

  // PaymentPlanError wraps myPaymentPlan's Failure variant for any code other than
  // NO_ACTIVE_PAYMENT_PLAN (that one resolves to the empty state, not an error — see requestPaymentPlan).
  const errorCode = error instanceof PaymentPlanError ? error.code : extractGqlErrorCode(error);

  const adjustPlanTooltip = (
    <TextTooltip
      icon="info"
      label={t('payments.paymentSummary.paymentPlan.adjustPlan')}
      content={t('payments.paymentSummary.paymentPlan.adjustPlanTooltip', {
        phone: MDVIP_COMPANY_INFO.memberSupportPhone,
      })}
    />
  );

  const errorBlock = (
    <Alert variant="destructive">
      <Icon name="alertTriangle" weight="solid" />
      {/* The shared Alert's title defaults to a single-line clamp, sized for a full-width banner —
          override it here since this Alert renders inside a much narrower metric column. */}
      <AlertTitle className="line-clamp-none">{t('payments.paymentSummary.paymentPlan.errorTitle')}</AlertTitle>
      <AlertDescription>
        <p>{tError(errorCode)}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
          <Icon name="refreshCw" size="sm" /> {t('payments.paymentSummary.paymentPlan.retry')}
        </Button>
      </AlertDescription>
    </Alert>
  );

  if (layout === 'narrow') {
    if (isLoading) {
      return (
        <div className={className} aria-busy="true">
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="mt-2 h-8 w-20" />
        </div>
      );
    }
    return (
      <div className={className}>
        {isError ? (
          errorBlock
        ) : data ? (
          <>
            <div className="text-sm text-muted-foreground">{t('payments.paymentSummary.paymentPlan.label')}</div>
            <div className="text-2xl font-bold leading-tight text-foreground">{data.cadence}</div>
            <div className="mt-1.5 text-xs text-muted-foreground">
              {t('payments.paymentSummary.paymentPlan.annualTotal', { amount: formatCurrency(data.annualTotal) })}
            </div>
            <div className="mt-1.5">{adjustPlanTooltip}</div>
          </>
        ) : (
          <>
            <div className="text-sm text-muted-foreground">{t('payments.paymentSummary.paymentPlan.label')}</div>
            <div className="text-2xl font-bold leading-tight text-muted-foreground">{'—'}</div>
            <div className="mt-1.5 text-xs text-muted-foreground">{t('payments.paymentSummary.paymentPlan.none')}</div>
            <div className="mt-1.5">{adjustPlanTooltip}</div>
          </>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <Metric
        label={<Skeleton className="h-3.5 w-24" />}
        className={className}
        data-slot="metric-payment-plan"
        data-state="loading"
        aria-busy="true"
      >
        <Skeleton className="h-8 w-24" />
        <Skeleton className="mt-2 h-3 w-20" />
      </Metric>
    );
  }

  return (
    <Metric
      label={t('payments.paymentSummary.paymentPlan.label')}
      className={className}
      data-slot="metric-payment-plan"
      data-state={isError ? 'error' : data ? 'active' : 'none'}
    >
      {isError ? (
        errorBlock
      ) : data ? (
        <>
          <div className="text-2xl font-semibold leading-none text-foreground">{data.cadence}</div>
          <div className="mt-2 text-xs text-muted-foreground">
            {t('payments.paymentSummary.paymentPlan.annualTotal', { amount: formatCurrency(data.annualTotal) })}
          </div>
          <div className="mt-1">{adjustPlanTooltip}</div>
        </>
      ) : (
        <>
          <div className="text-2xl font-semibold leading-none text-muted-foreground">{'—'}</div>
          <div className="mt-2 text-xs text-muted-foreground">{t('payments.paymentSummary.paymentPlan.none')}</div>
          <div className="mt-1">{adjustPlanTooltip}</div>
        </>
      )}
    </Metric>
  );
}
